import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { DashboardTab } from './components/TabBar';
import { KPICards } from './components/KPICards';
import { ProjectsTableExtended, type ProjectsTableHandle } from './components/ProjectsTableExtended';
import { UsersTable } from './components/UsersTable';
import { ClientsList } from '../tenants/ClientsList';
import { useSuperAdminData } from './hooks/useSuperAdminData';
import { useHistoricalStats } from './hooks/useHistoricalStats';
import { usersApi } from '../users/api';
import { adminProjectsApi } from '../projects/admin-api';
import { useToast } from '../toast';
import type { User } from '../auth/types';
import './superadmin-dashboard.css';

/**
 * Dashboard principal para SuperAdmin
 *
 * @remarks
 * Dashboard minimalista de una sola página con:
 * - Tabs horizontales (Proyectos | Usuarios)
 * - KPI cards estilo Vercel/Linear
 * - Lista simple con bordes sutiles
 * - Modales para CRUD inline
 * - 100% WESTEER design system
 *
 * @example
 * ```tsx
 * // En App.tsx
 * <Route path="/admin/dashboard" element={<SuperAdminDashboard />} />
 * ```
 */
export function SuperAdminDashboard(): JSX.Element {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: DashboardTab =
    rawTab === 'clients' ? 'clients' : rawTab === 'users' ? 'users' : 'projects';
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);

  const projectsTableRef = useRef<ProjectsTableHandle>(null);

  const { projects: _projects, tenants: _tenants, stats, loading, error, refresh } = useSuperAdminData();
  const { saveSnapshot } = useHistoricalStats();
  const { showToast } = useToast();

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const usersData = await usersApi.list();
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (rawTab === 'users') {
      void loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab]);

  /**
   * Guardar snapshot de estadísticas en localStorage
   *
   * @remarks
   * - Se guarda automáticamente cada vez que se cargan los datos
   * - El hook useHistoricalStats evita duplicados (máximo 1 cada 24h)
   * - Se usa para calcular indicadores de cambio en KPI Cards
   */
  useEffect(() => {
    if (!loading && stats) {
      saveSnapshot({
        totalProjects: stats.totalProjects,
        activeProjects: stats.activeProjects,
        totalTenants: stats.totalTenants,
        activeTenants: stats.activeTenants,
        totalUsers: stats.totalUsers,
        activeUsersCount: stats.activeUsersCount,
      });
    }
  }, [loading, stats, saveSnapshot]);

  const [reconcilingStuck, setReconcilingStuck] = useState(false);

  const handleReconcileStuck = async () => {
    try {
      setReconcilingStuck(true);
      const result = await adminProjectsApi.reconcileStuck();
      if (result.fixed === 0) {
        showToast('success', 'No se encontraron proyectos atascados');
      } else {
        showToast('success', `${result.fixed} proyecto(s) corregidos: ${result.details.join(', ')}`);
        await refresh();
        await projectsTableRef.current?.reload?.();
      }
    } catch (err) {
      showToast('error', 'Error al reconciliar proyectos atascados');
    } finally {
      setReconcilingStuck(false);
    }
  };

  // Handlers para proyectos
  const handleCreateProject = () => {
    navigate('/admin/projects/new');
  };

  const handleEditProject = (projectId: string) => {
    navigate(`/admin/projects/${projectId}/edit`);
  };

  const handleDeleteProject = async (projectId: string) => {
    // Prevent concurrent deletions - show feedback to user
    if (deletingProjectId !== null) {
      showToast('warning', 'Esperá a que termine la eliminación en curso');
      return;
    }

    try {
      setDeletingProjectId(projectId);
      await adminProjectsApi.delete(projectId);

      await Promise.all([
        refresh(),
        projectsTableRef.current?.reload?.()
      ]);

      showToast('success', 'Proyecto eliminado exitosamente');
    } catch (err: any) {
      const is401 = err?.response?.status === 401 || err?.status === 401;

      if (is401) {
        showToast('error', 'Sesión expirada. Recargá la página para continuar.');
      } else {
        showToast('error', t('dashboard.error.deletingProject'));
      }
    } finally {
      setDeletingProjectId(null);
    }
  };

  // Handlers para usuarios
  const handleDeleteUser = async (userId: string) => {
    try {
      await usersApi.delete(userId);
      await loadUsers(); // Recargar usuarios
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(t('dashboard.error.deletingUser'));
    }
  };

  return (
    <div className="superadmin-dashboard">
      <KPICards stats={stats} loading={loading} />

      {/* Error State */}
      {error && (
        <div className="dashboard-error" role="alert">
          <p>{t('dashboard.error.loadingData')}: {error}</p>
          <button type="button" onClick={refresh} className="btn-secondary">
            {t('dashboard.error.retry')}
          </button>
        </div>
      )}

      {/* Content Area - Conditional Render basado en tab activo */}
      <div className="dashboard-content">
        {activeTab === 'projects' && (
          <>
            <ProjectsTableExtended
              ref={projectsTableRef}
              deletingProjectId={deletingProjectId}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
              onCreate={handleCreateProject}
              onReconcileStuck={handleReconcileStuck}
              reconcilingStuck={reconcilingStuck}
            />
          </>
        )}

        {activeTab === 'clients' && <ClientsList />}

        {activeTab === 'users' && (
          <UsersTable
            users={users}
            loading={usersLoading}
            onDelete={handleDeleteUser}
          />
        )}
      </div>
    </div>
  );
}
